import { ChevronDown, Search, X } from 'lucide-react';
import { useMemo } from 'react';
import type { Category } from '../../data/types';
import { cn } from '../../lib/cn';

export type DateRangeKey =
  | 'period'
  | 'month'
  | '30d'
  | '90d'
  | 'year'
  | 'all';

const DATE_RANGE_OPTIONS: { value: DateRangeKey; label: string }[] = [
  { value: 'period', label: 'This period' },
  { value: 'month', label: 'This month' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'year', label: 'This year' },
  { value: 'all', label: 'All time' },
];

export type Filters = {
  search: string;
  categoryId: string;
  dateRange: DateRangeKey;
};

type Props = {
  filters: Filters;
  onChange: (next: Filters) => void;
  categories: Category[];
  /** Range start/end resolved by the parent for the date-pill display. */
  rangeStart: Date | null;
  rangeEnd: Date | null;
};

const RANGE_LABEL: Record<DateRangeKey, string> = {
  period: 'This period',
  month: 'This month',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  year: 'This year',
  all: 'All time',
};

function formatRange(
  range: DateRangeKey,
  start: Date | null,
  end: Date | null,
): { primary: string; secondary: string | null } {
  if (range === 'period' && start && end) {
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    return { primary: `${fmt(start)} – ${fmt(end)}`, secondary: 'This period' };
  }
  if (range === 'month' && start) {
    return {
      primary: start.toLocaleDateString(undefined, { month: 'long' }),
      secondary: null,
    };
  }
  return { primary: RANGE_LABEL[range], secondary: null };
}

/**
 * Search input plus two equal-width labeled pills (Category, Range).
 * Each pill renders custom two-line content with a chevron and uses an
 * absolutely-positioned native <select> at opacity 0 so taps still
 * trigger the system picker.
 */
export function ActivityFilters({
  filters,
  onChange,
  categories,
  rangeStart,
  rangeEnd,
}: Props) {
  const selectedCategoryName = useMemo(() => {
    if (filters.categoryId === 'all') return 'All categories';
    return (
      categories.find((c) => c.id === filters.categoryId)?.name ??
      'All categories'
    );
  }, [filters.categoryId, categories]);

  const expense = useMemo(
    () =>
      categories.filter((c) => c.type === 'expense').sort((a, b) => a.order - b.order),
    [categories],
  );
  const income = useMemo(
    () =>
      categories.filter((c) => c.type === 'income').sort((a, b) => a.order - b.order),
    [categories],
  );

  const range = formatRange(filters.dateRange, rangeStart, rangeEnd);

  return (
    <div className="flex flex-col gap-3 mb-card-gap">
      <label
        className={cn(
          'relative flex items-center h-11 rounded-button bg-surface-secondary',
          'focus-within:ring-2 focus-within:ring-accent/40',
        )}
      >
        <Search
          size={16}
          strokeWidth={1.5}
          className="absolute left-3 text-text-tertiary pointer-events-none"
        />
        <input
          type="text"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search transactions"
          autoCapitalize="none"
          spellCheck={false}
          className="w-full h-full pl-10 pr-10 bg-transparent text-[15px] text-text-primary placeholder:text-text-tertiary focus:outline-none"
        />
        {filters.search ? (
          <button
            type="button"
            onClick={() => onChange({ ...filters, search: '' })}
            aria-label="Clear search"
            className="absolute right-3 w-6 h-6 rounded-pill inline-flex items-center justify-center text-text-tertiary"
          >
            <X size={14} strokeWidth={1.5} />
          </button>
        ) : null}
      </label>

      <div className="flex gap-3">
        <div className="relative flex-1 h-11 rounded-button bg-surface-secondary px-3 flex items-center justify-between">
          <span className="text-[14px] font-medium text-text-primary truncate">
            {selectedCategoryName}
          </span>
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            className="text-text-tertiary shrink-0 ml-2"
          />
          <select
            value={filters.categoryId}
            onChange={(e) =>
              onChange({ ...filters, categoryId: e.target.value })
            }
            aria-label="Category"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
          >
            <option value="all">All categories</option>
            <optgroup label="Expense">
              {expense.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
            <optgroup label="Income">
              {income.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        <div className="relative flex-1 h-11 rounded-button bg-surface-secondary px-3 flex items-center justify-between">
          <span className="flex flex-col items-start min-w-0 leading-tight">
            <span className="text-[14px] font-medium text-text-primary truncate">
              {range.primary}
            </span>
            {range.secondary ? (
              <span className="text-[10px] text-text-tertiary truncate">
                {range.secondary}
              </span>
            ) : null}
          </span>
          <ChevronDown
            size={14}
            strokeWidth={1.75}
            className="text-text-tertiary shrink-0 ml-2"
          />
          <select
            value={filters.dateRange}
            onChange={(e) =>
              onChange({
                ...filters,
                dateRange: e.target.value as DateRangeKey,
              })
            }
            aria-label="Date range"
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer appearance-none"
          >
            {DATE_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
